import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useResetUserPassword } from '@/api/hooks/useUsers';

interface ResetPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: number;
  userDisplayName?: string;
}

export function ResetPasswordDialog({
  open,
  onOpenChange,
  userId,
  userDisplayName,
}: ResetPasswordDialogProps) {
  const { t } = useTranslation(['catalogs', 'common']);
  const resetMutation = useResetUserPassword();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<{ newPassword?: string; confirmPassword?: string }>({});

  useEffect(() => {
    if (!open) {
      setNewPassword('');
      setConfirmPassword('');
      setErrors({});
    }
  }, [open]);

  const handleSubmit = async () => {
    const nextErrors: { newPassword?: string; confirmPassword?: string } = {};
    if (newPassword.length < 8) {
      nextErrors.newPassword = t('catalogs:users.form.passwordTooShort');
    }
    if (newPassword !== confirmPassword) {
      nextErrors.confirmPassword = t('catalogs:users.form.passwordMismatch');
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});
    try {
      await resetMutation.mutateAsync({ userId, newPassword, confirmPassword });
      toast.success(t('catalogs:users.form.resetPasswordSuccess'));
      onOpenChange(false);
    } catch (error) {
      const axiosErr = error as AxiosError<{ message?: string }>;
      toast.error(
        axiosErr?.response?.data?.message ?? t('common:errors.unknown', 'Error desconocido')
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('catalogs:users.form.resetPasswordTitle')}</DialogTitle>
          <DialogDescription>
            {t('catalogs:users.form.resetPasswordDescription')}
            {userDisplayName ? ` (${userDisplayName})` : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2 min-w-0">
          <div>
            <Label htmlFor="resetNewPassword">{t('catalogs:users.form.newPassword')}</Label>
            <Input
              id="resetNewPassword"
              type="password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                if (errors.newPassword) setErrors((p) => ({ ...p, newPassword: undefined }));
              }}
              autoComplete="new-password"
            />
            {errors.newPassword && (
              <p className="text-destructive text-xs mt-1">{errors.newPassword}</p>
            )}
          </div>
          <div>
            <Label htmlFor="resetConfirmPassword">
              {t('catalogs:users.form.confirmPassword')}
            </Label>
            <Input
              id="resetConfirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (errors.confirmPassword)
                  setErrors((p) => ({ ...p, confirmPassword: undefined }));
              }}
              autoComplete="new-password"
            />
            {errors.confirmPassword && (
              <p className="text-destructive text-xs mt-1">{errors.confirmPassword}</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={resetMutation.isPending}
          >
            {t('common:buttons.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={resetMutation.isPending}>
            {resetMutation.isPending
              ? t('common:status.saving')
              : t('catalogs:users.form.resetPasswordButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
